import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function AdminSettings({ refresh }) {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => setF(r.data));
  }, []);

  if (!f) return <p className="text-neutral-400">Loading settings…</p>;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/admin/settings", f);
      toast.success("Settings saved");
      refresh?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const I = (k, label, placeholder = "") => (
    <div>
      <label className="text-[10px] font-bold uppercase tracking-[0.3em] text-neutral-400">{label}</label>
      <input value={f[k] ?? ""} placeholder={placeholder} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`settings-${k}`} className="mt-2 w-full bg-black border border-white/15 px-3 py-2 text-white focus:outline-none focus:border-gold placeholder:text-neutral-600" />
    </div>
  );

  return (
    <form onSubmit={save} className="space-y-6" data-testid="admin-settings-form">
      <Section title="Social Links">
        <div className="grid md:grid-cols-2 gap-3">
          {I("patreon_url", "Patreon URL", "https://patreon.com/SVNNetwork")}
          {I("youtube_url", "YouTube URL")}
          {I("facebook_url", "Facebook URL")}
          {I("instagram_url", "Instagram URL")}
          {I("tiktok_url", "TikTok URL")}
          {I("spotify_url", "Spotify URL")}
          {I("apple_podcasts_url", "Apple Podcasts URL")}
        </div>
      </Section>

      <Section title="Contact">
        <div className="grid md:grid-cols-2 gap-3">
          {I("contact_email", "General contact email", "hello@svnlive.com")}
          {I("sponsor_email", "Sponsorship email", "partners@svnlive.com")}
        </div>
      </Section>

      <Section title="SEO & Footer">
        {I("seo_title", "SEO title")}
        <div>
          <label className="text-[10px] font-bold uppercase tracking-[0.3em] text-neutral-400">SEO description</label>
          <textarea rows={2} value={f.seo_description ?? ""} onChange={(e) => setF({ ...f, seo_description: e.target.value })} data-testid="settings-seo_description" className="mt-2 w-full bg-black border border-white/15 px-3 py-2 text-white focus:outline-none focus:border-gold" />
        </div>
        {I("footer_text", "Footer text")}
      </Section>

      <Section title="Analytics" hint="Paste your Google Analytics 4 measurement ID (e.g., G-XXXXXXXXXX). The site will inject the GA tag automatically.">
        {I("ga_tracking_id", "Google Analytics measurement ID", "G-XXXXXXXXXX")}
        <div>
          <label className="text-[10px] font-bold uppercase tracking-[0.3em] text-neutral-400">Additional tracking script (optional)</label>
          <textarea
            rows={3}
            placeholder="<!-- e.g., Plausible script tag — paste raw HTML here. Will be injected as-is. -->"
            value={f.ga_script ?? ""}
            onChange={(e) => setF({ ...f, ga_script: e.target.value })}
            data-testid="settings-ga_script"
            className="mt-2 w-full bg-black border border-white/15 px-3 py-2 text-white font-mono text-xs focus:outline-none focus:border-gold placeholder:text-neutral-600"
          />
        </div>
      </Section>

      <button type="submit" disabled={saving} data-testid="settings-save" className="bg-svn-red text-white font-black uppercase tracking-widest text-sm px-8 py-4 hover:bg-red-700 disabled:opacity-60">
        {saving ? "Saving…" : "Save Settings"}
      </button>

      <ResetSection />
    </form>
  );
}

function ResetSection() {
  const [opts, setOpts] = useState({ wipe_episodes: false, wipe_sponsors: false, wipe_submissions: false, wipe_pageviews: false });
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const anyChecked = Object.values(opts).some(Boolean);

  const run = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!anyChecked) return toast.error("Select at least one category to wipe");
    if (confirm !== "WIPE") return toast.error('Type WIPE to confirm');
    if (!window.confirm("This permanently deletes the selected data. Continue?")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/reset", { ...opts, confirm });
      toast.success(`Reset complete. ${JSON.stringify(data.summary)}`);
      setConfirm("");
      setOpts({ wipe_episodes: false, wipe_sponsors: false, wipe_submissions: false, wipe_pageviews: false });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-[#0F0F0F] border border-svn-red/30 p-6 mt-10" data-testid="admin-reset-section">
      <p className="text-xs font-bold uppercase tracking-[0.3em] text-svn-red mb-1">Danger Zone — Pre-Launch Reset</p>
      <p className="text-xs text-neutral-500 mb-4">
        Clear placeholder/demo data before going public. Shows and Settings are not affected. Type <span className="font-mono text-svn-red">WIPE</span> to confirm.
      </p>
      <div className="grid md:grid-cols-2 gap-2 mb-4">
        {[
          ["wipe_episodes", "Delete all episodes (incl. demo)"],
          ["wipe_sponsors", "Delete all sponsors (incl. demo)"],
          ["wipe_submissions", "Delete contacts + inquiries + newsletter subs"],
          ["wipe_pageviews", "Delete page-view analytics history"],
        ].map(([k, label]) => (
          <label key={k} className="flex items-center gap-3 text-sm text-neutral-300">
            <input type="checkbox" checked={opts[k]} onChange={(e) => setOpts({ ...opts, [k]: e.target.checked })} className="accent-red-500" data-testid={`reset-${k}`} />
            {label}
          </label>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <input
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder='Type "WIPE" to confirm'
          data-testid="reset-confirm-input"
          className="bg-black border border-svn-red/40 px-3 py-2 text-white focus:outline-none focus:border-svn-red font-mono text-sm"
        />
        <button
          type="button"
          onClick={run}
          disabled={busy || !anyChecked || confirm !== "WIPE"}
          data-testid="reset-run-btn"
          className="bg-svn-red text-white font-black uppercase tracking-widest text-xs px-5 py-2.5 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {busy ? "Wiping…" : "Run Reset"}
        </button>
      </div>
    </div>
  );
}

function Section({ title, hint, children }) {
  return (
    <div className="bg-[#0F0F0F] border border-white/10 p-6">
      <p className="text-xs font-bold uppercase tracking-[0.3em] text-gold mb-1">{title}</p>
      {hint && <p className="text-xs text-neutral-500 mb-4">{hint}</p>}
      <div className="space-y-3">{children}</div>
    </div>
  );
}
